import { all, select, call, takeLatest, put } from 'redux-saga/effects';
import moment from 'moment';
import { logger } from '../../helpers/logger';
import { RootState, store } from '../store';
import actions from './actions';
import appActions from '../app/actions';
import authActions from '../auth/actions';
import featureFlagsActions from '../featureFlags/actions';
import permissionsActions from '../permissions/actions';
import validateSessionActions from '../validateSession/actions';
import userProfileActions from '../userProfile/actions';
import { fetchFeatureFlags as fetchFeatureFlagsSaga } from '../featureFlags/saga';
import { fetchPermissions as fetchPermissionsSaga } from '../permissions/saga';
import { checkLoginMethodsRequest, performCheckAuthorization } from '../auth/saga';
import { validateSessionRequest } from '../validateSession/saga';
import { userProfileRequest } from '../userProfile/saga';
import { onLoadTheme } from '../app/saga';
import { getOrganizationCode } from '../../settings/organizationSettings';
import { LAST_THEME_FETCH_DATE_KEY, THEME_CONFIG_KEY } from '../../helpers/localStorageKeys.const';
import { ROUTES } from '../../containers/App/routes';

function* checkLoginMethods(): Generator<any, void, any> {
  const featureFlags = yield select((state: RootState) => state.FeatureFlags.flags);

  if (!featureFlags?.unified_login_enabled) {
    yield put(authActions.checkLoginMethodsRequest());
    yield call(checkLoginMethodsRequest);
  }
}

// todo
function* loadTheme(): Generator<any, void, any> {
  const lastThemeFetchDate = localStorage.getItem(LAST_THEME_FETCH_DATE_KEY);
  const themeConfigRaw = localStorage.getItem(THEME_CONFIG_KEY);
  const cachedTheme = JSON.parse(themeConfigRaw || '{}');
  const isUnifiedLoginEnabled = store?.getState()?.FeatureFlags.flags?.unified_login_enabled;
  const isOrganizationCodeChanged =
    cachedTheme.organizationCode?.toLowerCase() !== getOrganizationCode()?.toLowerCase();

  if (!lastThemeFetchDate || !themeConfigRaw || (isOrganizationCodeChanged && !isUnifiedLoginEnabled)) {
    // fetch theme if not fetched yet or if organization code changed
    yield put(appActions.loadTheme());
    yield call(onLoadTheme);
  } else {
    // check if last fetch was more than 3 hours ago
    const isThemeFetchedRecently = moment().subtract(3, 'h').isBefore(moment(lastThemeFetchDate));

    if (!isThemeFetchedRecently) {
      yield put(appActions.loadTheme());
      yield call(onLoadTheme);
    }
  }
}

function* fetchFeatureFlags(): Generator<any, void, any> {
  yield put(featureFlagsActions.fetchFeatureFlags());
  yield call(fetchFeatureFlagsSaga);
}

function* fetchPermissions(): Generator<any, void, any> {
  yield put(permissionsActions.fetchPermissions());
  yield call(fetchPermissionsSaga);
}

function* validateSession(): Generator<any, void, any> {
  const featureFlags = yield select((state: RootState) => state.FeatureFlags.flags);

  if (featureFlags?.unified_login_enabled) {
    yield put(validateSessionActions.validateSessionRequest());
    yield call(validateSessionRequest);
  }
}

function* fetchUserProfile(): Generator<any, void, any> {
  const featureFlags = yield select((state: RootState) => state.FeatureFlags.flags);

  if (featureFlags?.unified_login_enabled) {
    yield put(userProfileActions.userProfileRequest());
    yield call(userProfileRequest);
  }
}

function* checkAuthorization(): Generator<any, void, any> {
  yield call(performCheckAuthorization);
}

function* appBootstrapSuccess(): Generator<any, void, any> {
  yield put(actions.appBootstrapSuccess());
}

function* appBootstrapError(): Generator<any, void, any> {
  yield put(actions.appBootstrapError());
}

function* appBootstrap(): Generator<any, void, any> {
  const isAdminSignIn = window.location.pathname === `/${ROUTES.ADMIN_SIGN_IN}`;

  try {
    yield call(fetchFeatureFlags);
    yield call(checkAuthorization);

    if (!isAdminSignIn) {
      yield call(validateSession);
      yield call(fetchUserProfile);
      yield call(fetchPermissions);
    }

    yield call(loadTheme);
    yield call(checkLoginMethods);
    yield call(appBootstrapSuccess);
  } catch (e) {
    logger.error(e);
    yield call(appBootstrapError);
  }
}

export default function* rootSaga() {
  yield all([takeLatest(actions.APP_BOOTSTRAP, appBootstrap)]);
}
